import * as Api from '@ViewModels';
import { AiButton } from '@HoudiniUI/AiButton';
import { useRef, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AIPromptUsage, IAIPromptTemplate } from '../../../../models/AIPromptTemplate';
import { useAIPromptLibrary, useMyAIPrompts } from '../../../../queries/AIPrompt';
import {
	Dropdown,
	DropdownContent,
	DropdownGroup,
	DropdownGroupLabel,
	DropdownItem,
	DropdownSeparator,
	DropdownSubMenu,
	DropdownSubTrigger,
	DropdownTrigger,
} from '@HoudiniUI/Dropdown';
import { PeopleIcon } from '@WebComponents/svgs/icons/PeopleIcon';
import { SearchIcon2 } from '@WebComponents/svgs/icons/SearchIcon2';
import { SparklesIcon } from '@WebComponents/svgs/icons/SparklesIcon';
import { styleSheet } from './styles';
import { usePromptIndustry } from './usePromptIndustry';

interface IProps {
	/** Also skips loading: a read-only view has no use for prompts it cannot insert. */
	disabled?: boolean;
	/** Where focus goes once the menu closes: the field, so a picked prompt can be typed over straight away. */
	finalFocus?: React.RefObject<HTMLElement | null>;
	impersonationContext?: Api.IImpersonationContext;
	onBrowseAll: () => void;
	onPromptSelected: (prompt: IAIPromptTemplate) => void;
	usage: AIPromptUsage;
}

/**
 * The field's Help me write this menu: Levitate's samples for the account's industry, the team's saved prompts, and
 * the side panel for everything else.
 */
export const HelpMeWriteMenu: React.FC<IProps> = ({
	disabled,
	finalFocus,
	impersonationContext,
	onBrowseAll,
	onPromptSelected,
	usage,
}) => {
	const userSession = useUserSession();
	const currentUserId = userSession?.user?.id;
	const { industry, industryName } = usePromptIndustry();
	// Loaded once someone reaches for the menu, not on mount: a canvas can hold a field per step, and most are never
	// opened. Hover or focus starts the load, so the lists are usually there by the time the menu opens.
	const [isWanted, setIsWanted] = useState(false);
	const want = () => setIsWanted(true);
	const shouldLoad = isWanted && !disabled;
	const libraryQuery = useAIPromptLibrary({ enabled: shouldLoad, impersonationContext, industry, usage });
	const mineQuery = useMyAIPrompts({ enabled: shouldLoad, impersonationContext, usage });

	// Browse all opens the side panel only once this menu has finished closing. The panel closes on any press outside
	// it, and the click that chose Browse all is still going on when the menu reacts to it: open the panel straight
	// away and that same click can reach it as an outside press and shut it again.
	const browseRequested = useRef(false);
	const onOpenChangeComplete = (open: boolean) => {
		if (!open && browseRequested.current) {
			browseRequested.current = false;
			onBrowseAll();
		}
	};

	const samples = libraryQuery.data ?? [];
	const saved = mineQuery.data ?? [];
	const yours = saved.filter(prompt => !!currentUserId && prompt.creator?.id === currentUserId);
	const team = saved.filter(prompt => !currentUserId || prompt.creator?.id !== currentUserId);

	const item = (prompt: IAIPromptTemplate) => (
		<DropdownItem key={prompt.id} onClick={() => onPromptSelected(prompt)} styles={[styleSheet.helpItem]}>
			{prompt.name}
		</DropdownItem>
	);

	return (
		<Dropdown onOpenChange={open => open && want()} onOpenChangeComplete={onOpenChangeComplete}>
			<DropdownTrigger
				disabled={disabled}
				onFocus={want}
				onPointerEnter={want}
				render={<AiButton size='xs' title='Help me write this' type='button' />}
			/>
			<DropdownContent align='start' finalFocus={finalFocus} styles={[styleSheet.helpMenu]}>
				<DropdownSubMenu>
					<DropdownSubTrigger styles={[styleSheet.helpCategory]}>
						<SparklesIcon height={16} width={16} />
						Samples
					</DropdownSubTrigger>
					<DropdownContent styles={[styleSheet.helpSubmenu]}>
						<DropdownGroup>
							{industryName ? <DropdownGroupLabel>{`For ${industryName.toLowerCase()}`}</DropdownGroupLabel> : null}
							{libraryQuery.isLoading ? <DropdownItem disabled>Loading samples…</DropdownItem> : null}
							{!libraryQuery.isLoading && samples.length === 0 ? (
								<DropdownItem disabled>There are no samples for this industry yet.</DropdownItem>
							) : null}
							{samples.map(item)}
						</DropdownGroup>
					</DropdownContent>
				</DropdownSubMenu>

				<DropdownSubMenu>
					<DropdownSubTrigger styles={[styleSheet.helpCategory]}>
						<PeopleIcon height={16} width={16} />
						My prompts
					</DropdownSubTrigger>
					<DropdownContent styles={[styleSheet.helpSubmenu]}>
						{mineQuery.isLoading ? <DropdownItem disabled>Loading your prompts…</DropdownItem> : null}
						{!mineQuery.isLoading && saved.length === 0 ? (
							<DropdownItem disabled>Nobody here has saved a prompt yet.</DropdownItem>
						) : null}
						{yours.length > 0 ? (
							<DropdownGroup>
								<DropdownGroupLabel>Yours</DropdownGroupLabel>
								{yours.map(item)}
							</DropdownGroup>
						) : null}
						{yours.length > 0 && team.length > 0 ? <DropdownSeparator /> : null}
						{team.length > 0 ? (
							<DropdownGroup>
								<DropdownGroupLabel>Shared with the team</DropdownGroupLabel>
								{team.map(item)}
							</DropdownGroup>
						) : null}
					</DropdownContent>
				</DropdownSubMenu>

				<DropdownSeparator />
				<DropdownItem onClick={() => (browseRequested.current = true)} styles={[styleSheet.helpCategory]}>
					<SearchIcon2 height={16} width={16} />
					Browse all prompts
				</DropdownItem>
			</DropdownContent>
		</Dropdown>
	);
};
